import { doc, getDoc, setDoc, increment, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db, appId } from '../config/firebase';

const buildCacheDocRef = (userId, propertyId, type) =>
  doc(db, `artifacts/${appId}/users/${userId}/analysisCache`, `${propertyId}_${type}`);

const metricsRef = () =>
  doc(db, `artifacts/${appId}/analytics/cacheMetrics`);

/**
 * Check the cache for an existing result.
 * Returns the cached markdown on hit, or null on miss / error.
 * Never throws — cache failures fall through silently to the Gemini call.
 */
export const getCachedResult = async ({ userId, propertyId, type, hash, inputLength }) => {
  try {
    if (!db || !userId) return null;

    const docRef = buildCacheDocRef(userId, propertyId, type);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;

    const data = docSnap.data();

    const isHashMatch = data.dataHash === hash;
    const isLengthMatch = data.inputLength === inputLength;
    const isNotExpired = data.expiresAt?.toDate?.() > new Date();

    if (!isHashMatch || !isLengthMatch || !isNotExpired) return null;

    setDoc(docRef, { hitCount: increment(1), lastHitAt: serverTimestamp() }, { merge: true })
      .catch(e => console.warn('Cache hit update failed:', e));

    setDoc(metricsRef(), { totalHits: increment(1), lastUpdated: serverTimestamp() }, { merge: true })
      .catch(e => console.warn('Metrics hit update failed:', e));

    return data.resultMarkdown;
  } catch (error) {
    console.warn('Cache read failed:', error);
    return null;
  }
};

/**
 * Write a Gemini result to the cache.
 * Uses setDoc to overwrite any previous entry for this property+type (deterministic doc ID).
 * Fire-and-forget from the caller's perspective — never throws.
 */
export const setCachedResult = async ({ userId, propertyId, type, hash, inputLength, resultMarkdown, ttlDays }) => {
  try {
    if (!db || !userId) return;

    const safeTTL = Math.max(1, ttlDays || 30);
    const docRef = buildCacheDocRef(userId, propertyId, type);

    await setDoc(docRef, {
      propertyId,
      type,
      dataHash: hash,
      inputLength,
      resultMarkdown,
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + safeTTL * 24 * 60 * 60 * 1000)),
      ttlDays: safeTTL,
      hitCount: 0,
      lastHitAt: null,
    });

    setDoc(metricsRef(), { totalMisses: increment(1), lastUpdated: serverTimestamp() }, { merge: true })
      .catch(e => console.warn('Metrics miss update failed:', e));
  } catch (error) {
    console.warn('Cache write failed:', error);
  }
};
